export default () => {
  /**
   * Set the same height on multiple elements in the document tree.
   * Set the data-equal="<name>" attribute on the elements that should be of equal height.
   * Whereby <name> is unique to a group elements you want of equal height.
   *
   * Additional attributes are:
   *
   * 	- data-equal-exclude="<breakpoints>"
   *    Excludes the element if any breakpoints matches with the currently active breakpoint.
   * 		Where <breakpoints> is a comma separated list of breakpoints.
   * 		Breakpoints can be either xs, sm, md, lg, or xl.
   *
   * - data-equal-first
   * 	 Excludes the element if it is not the first element in the parent element.
   *
   * - data-equal-last
   * 	 Excludes the element if it is not the last element in the parent element.
   */

  const NAME = 'equal';
  const NAME_EXCLUDE_BREAKPOINTS = 'exclude';
  const NAME_EXCLUDE_FIRST = 'first';
  const NAME_EXCLUDE_LAST = 'last';
  const attributeName = 'data-' + NAME;
  const attributeNameExcludeBreakpoints = attributeName + '-' + NAME_EXCLUDE_BREAKPOINTS;
  const attributeNameExcludeFirst = attributeName + '-' + NAME_EXCLUDE_FIRST;
  const attributeNameExcludeLast = attributeName + '-' + NAME_EXCLUDE_LAST;

  // Return the breakpoint as text, either xs, sm, md, lg, or xl.
  const getBreakpoint = function () {
    if (window.innerWidth >= 1200) {
      return 'xl';
    } else if (window.innerWidth >= 992) {
      return 'lg';
    } else if (window.innerWidth >= 768) {
      return 'md';
    } else if (window.innerWidth >= 576) {
      return 'sm';
    }
    return 'xs';
  };

  // Return whether the node should be excluded from having it's height read or updated.
  const shouldExcludeNode = function (node) {
    // Exclude if element should be excluded based of position in parent node.
    if (node.hasAttribute(attributeNameExcludeFirst) && node.parentNode.firstElementChild !== node) {
      return true;
    } else if (node.hasAttribute(attributeNameExcludeLast) && node.parentNode.lastElementChild !== node) {
      return true;
    }

    // Exclude if element should be excluded based of the current breakpoint.
    if (node.hasAttribute(attributeNameExcludeBreakpoints)) {
      // Check if any breakpoints match.
      const excludeBreakpoints = node.getAttribute(attributeNameExcludeBreakpoints).split(',').map(breakpoint => breakpoint.trim().toLowerCase());
      if (excludeBreakpoints.indexOf(getBreakpoint()) >= 0) {
        return true;
      }
    }
    return false;
  };

  // Scans the document and equalizes the height of elements with matching attribute values.
  const setNodeHeights = function () {
    // Get elements to process.
    let nodes = document.querySelectorAll('[' + attributeName + ']');
    // Converts the NodeList to an Array.
    nodes = Array.prototype.slice.call(nodes).reverse();

    // Get the maximum heights of elements with the equal class by attribute value.
    const nodeCountById = {};
    const maximumHeightsById = {};
    // Iteration happens in reverse since we will be removing elements that do not match the criteria.
    for (let i = nodes.length - 1; i >= 0; i--) {
      const node = nodes[i];
      if (shouldExcludeNode(node)) {
        // Remove from list.
        nodes.splice(i, 1);
        continue;
      }
      // Use the data attribute's value as category identifier.
      const id = node.getAttribute(attributeName);
      // Increment element counter.
      if (!Object.prototype.hasOwnProperty.call(nodeCountById, id)) {
        nodeCountById[id] = 1;
      } else {
        nodeCountById[id]++;
      }
      // Set maximum height if none set yet, or the current element's height is larger.
      if (!Object.prototype.hasOwnProperty.call(maximumHeightsById, id) || node.clientHeight > maximumHeightsById[id]) {
        maximumHeightsById[id] = node.clientHeight;
      }
    }

    // Set height of the elements.
    for (let i = nodes.length - 1; i >= 0; i--) {
      const node = nodes[i];
      // Use the data attribute's value as category identifier.
      const id = node.getAttribute(attributeName);
      // Skip if there is only one element with this id.
      if (nodeCountById[id] <= 1) {
        continue;
      }
      node.style.height = maximumHeightsById[id] + 'px';
    }
  };
  const _updateNodeHeights = () => {
    // Remove all height restrictions applied by the equal class.
    let nodes = document.querySelectorAll('[' + attributeName + ']');
    // Converts the NodeList to an Array.
    nodes = Array.prototype.slice.call(nodes);
    // Remove height style of all the elements.
    for (let i = 0; i < nodes.length; i++) {
      nodes[i].style.height = null;
    }

    // Re-add height restrictions in the net frame.
    requestAnimationFrame(setNodeHeights);
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
  };
  let resizeTimeout = null;
  const updateNodeHeights = () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(_updateNodeHeights, 10);
  };
  // Update after resizing the window, the font-size, or when items are loaded.
  window.addEventListener('resize', updateNodeHeights);
  document.body.addEventListener('accessibility-font-size-changed', _updateNodeHeights);
  document.body.addEventListener('blog-lazyloaded', _updateNodeHeights);
  document.body.addEventListener('moderation-queue-lazyloaded', _updateNodeHeights);
  document.body.addEventListener('gallery-lazyloaded', _updateNodeHeights);
  document.body.addEventListener('product-lazyloaded', _updateNodeHeights);
  const updateOnResourceLoaded = () => {
    let loadingElements = 0;
    const loadedResource = () => {
      loadingElements--;
      if (loadingElements === 0) {
        updateNodeHeights();
      }
    };
    const processResource = node => {
      if (node.tagName === 'IMG' && node.complete || node.tagName === 'SCRIPT' && node.readyState === 'complete' || node.tagName === 'LINK' && node.sheet || node.tagName === 'VIDEO' && node.readyState >= 4 ||
      // Use HTMLMediaElement.readyState
      node.tagName === 'EMBED' && node.src && node.getSVGDocument() ||
      // Checking if an embed element has loaded content
      node.tagName === 'OBJECT' && node.contentDocument ||
      // Object has loaded content
      node.tagName === 'IFRAME' && node.contentWindow && node.contentDocument) {
        return;
      }
      node.addEventListener('load', loadedResource);
      node.addEventListener('error', loadedResource);
    };
    const resourceElements = Array.prototype.slice.call(document.body.querySelectorAll('embed,iframe,img,link[rel="stylesheet"],object,script,video'));
    for (let index = 0; index < resourceElements.length; index++) {
      processResource(resourceElements[index]);
    }
  };
  const updateClosestNodeHeights = node => {
    const updates = [];

    // Walk up the dom tree updating all equal elements along the way.
    const addClosestEqualNode = node => {
      const closest = node.parentNode.closest('[' + attributeName + ']');
      if (closest) {
        // Get all elements of the same identifier.
        const identifier = closest.getAttribute(attributeName);
        let nodes = document.querySelectorAll('[' + attributeName + '="' + identifier + '"]');
        // Converts the NodeList to an Array.
        nodes = Array.prototype.slice.call(nodes).reverse();

        // Remove the current height restriction of alle elements of that type.
        for (let i = nodes.length - 1; i >= 0; i--) {
          const node = nodes[i];
          node.style.height = null;
        }
        updates.push(() => {
          // Get the maximum height of the elements.
          let maximumHeight = null;
          if (nodes.length > 1) {
            for (let i = nodes.length - 1; i >= 0; i--) {
              const node = nodes[i];
              if (shouldExcludeNode(node)) {
                // Remove from list so it won't be updated.
                nodes.splice(i, 1);
                continue;
              }
              // Set maximum height if none set yet, or the current element's height is larger.
              if (!maximumHeight || node.clientHeight > maximumHeight) {
                maximumHeight = node.clientHeight;
              }
            }
          }
          // Update the hight of the elements.
          if (nodes.length > 1) {
            for (let i = nodes.length - 1; i >= 0; i--) {
              const node = nodes[i];
              node.style.height = maximumHeight + 'px';
            }
          }
        });
        addClosestEqualNode(closest);
      }
    };
    addClosestEqualNode(node);
    if (updates.length > 0) {
      requestAnimationFrame(() => {
        for (let i = 0; i < updates.length; i++) {
          updates[i]();
        }
      });
    }
  };
  document.body.addEventListener('shorten-text-expanding', event => {
    updateClosestNodeHeights(event.detail.target);
  });
  document.body.addEventListener('shorten-text-expanded', event => {
    updateClosestNodeHeights(event.detail.target);
  });

  // Update now.
  setNodeHeights();
  updateOnResourceLoaded();

  // Call update as soon as an element with the equal attribute has changed.
  const observer = new MutationObserver(function (mutations) {
    for (let i = 0; i < mutations.length; i++) {
      const mutation = mutations[i];

      // Check if equal attribute has changed.
      if (mutation.attributeName === attributeName) {
        setNodeHeights();
        updateOnResourceLoaded();
        return;
      }
      for (let j = 0; j < mutation.addedNodes.length; j++) {
        const addedNode = mutation.addedNodes[j];

        // Skip if not an element node.
        if (addedNode.nodeType !== 1) {
          continue;
        }

        // Check if the element contains a equal attribute.
        if (addedNode.hasAttribute(attributeName)) {
          setNodeHeights();
          updateOnResourceLoaded();
        }

        // Check if any child elements has the equal attribute.
        const equalNode = addedNode.querySelector('[' + attributeName + ']');
        if (equalNode) {
          setNodeHeights();
          updateOnResourceLoaded();
          return;
        }
      }
      for (let j = 0; j < mutation.removedNodes.length; j++) {
        const removedNode = mutation.removedNodes[j];

        // Skip if not an element node.
        if (removedNode.nodeType !== 1) {
          continue;
        }

        // Check if the element contains a equal attribute.
        if (removedNode.hasAttribute(attributeName)) {
          updateOnResourceLoaded();
          setNodeHeights();
        }

        // Check if any child elements has the equal attribute.
        const equalNode = removedNode.querySelector('[' + attributeName + ']');
        if (equalNode) {
          setNodeHeights();
          updateOnResourceLoaded();
          return;
        }
      }
    }
  });
  // Start observing the body for mutations.
  observer.observe(document.body, {
    attributes: true,
    childList: true,
    subtree: true
  });
};